import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import {
  findPattern,
  patternSteps,
  rowMarkerColorHex,
} from "../../../../src/lib/audio";
import { useStoreSelector } from "../../../../src/lib/store";
import { getLabActions } from "../labActions";
import { markersFromMap } from "../lib/goToMarkers";
import { projectStore } from "../store/projectStore";
import { patchUi, uiStore } from "../store/uiStore";

const PANEL_WIDTH = 420;

type Suggestion =
  | { kind: "row"; step: number; title: string; detail: string }
  | { kind: "marker"; step: number; title: string; detail: string; color: number };

/**
 * Command-palette jump: row number or row-marker label.
 */
export function GoToModal() {
  const open = useStoreSelector(uiStore, s => s.goToOpen);
  const clip = useStoreSelector(projectStore, s => s.clip);
  const activePatternId = useStoreSelector(uiStore, s => s.activePatternId);

  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const stepCount = useMemo(() => {
    const pattern = findPattern(clip, activePatternId);
    return pattern ? patternSteps(pattern) : 0;
  }, [clip, activePatternId]);

  const markers = useMemo(
    () => markersFromMap(findPattern(clip, activePatternId)?.rowMarkers),
    [clip, activePatternId],
  );

  const suggestions = useMemo(
    () => buildSuggestions(query, stepCount, markers),
    [query, stepCount, markers],
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    setQuery("");
    setActiveIndex(0);
    const id = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    setActiveIndex(suggestions.length > 0 ? 0 : -1);
  }, [suggestions]);

  useEffect(() => {
    if (!open) {
      return;
    }
    function onKey(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        close();
      }
    }
    function onPointerDown(event: PointerEvent): void {
      const panel = panelRef.current;
      if (event.target instanceof Node && panel?.contains(event.target)) {
        return;
      }
      close();
    }
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [open]);

  function close(): void {
    patchUi({ goToOpen: false });
    getLabActions()?.focusTracker?.();
  }

  function activate(index: number): void {
    const item = suggestions[index];
    if (!item) {
      return;
    }
    close();
    getLabActions()?.goToRow?.(item.step);
  }

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    activate(activeIndex);
  }

  function onInputKeyDown(event: ReactKeyboardEvent<HTMLInputElement>): void {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (suggestions.length === 0) {
        return;
      }
      setActiveIndex(i => (i + 1) % suggestions.length);
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (suggestions.length === 0) {
        return;
      }
      setActiveIndex(i => (i - 1 + suggestions.length) % suggestions.length);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close();
    }
  }

  if (!open) {
    return null;
  }

  const width = Math.min(PANEL_WIDTH, window.innerWidth - 24);
  const left = Math.max(12, (window.innerWidth - width) / 2);
  const top = Math.max(48, Math.min(120, window.innerHeight * 0.14));

  const emptyMessage = (() => {
    const q = query.trim();
    if (/^\d+$/.test(q)) {
      return stepCount <= 0
        ? "Pattern has no rows."
        : `No row ${q} (valid 0–${Math.max(0, stepCount - 1)}).`;
    }
    if (q) {
      return "No markers match.";
    }
    return "Type a row number, or pick a marker below when any exist.";
  })();

  return createPortal(
    <div
      ref={panelRef}
      className="goto-modal fixed z-[60] flex max-h-[min(52vh,420px)] flex-col overflow-hidden rounded-[10px] border border-[color-mix(in_srgb,var(--accent)_40%,var(--line))] bg-lab-panel text-[13px] text-slate-300 shadow-[0_28px_64px_rgba(0,0,0,0.55),0_0_0_1px_color-mix(in_srgb,var(--accent)_20%,transparent)]"
      role="dialog"
      aria-modal="true"
      aria-label="Go to row or marker"
      style={{ width, left, top }}
    >
      <form className="goto-modal-form shrink-0 border-b border-lab-line bg-lab-panel-bar" onSubmit={onSubmit}>
        <input
          ref={inputRef}
          type="search"
          className="w-full border-none bg-transparent px-[0.9rem] py-3 text-[14px] text-slate-100 outline-none placeholder:text-slate-500"
          placeholder="Row number or marker…"
          autoComplete="off"
          spellCheck={false}
          aria-autocomplete="list"
          aria-controls="goto-modal-list"
          aria-activedescendant={
            activeIndex >= 0 ? `goto-item-${activeIndex}` : undefined
          }
          value={query}
          onChange={event => setQuery(event.target.value)}
          onKeyDown={onInputKeyDown}
        />
      </form>
      {suggestions.length === 0 ? (
        <div className="px-[0.9rem] py-4 text-[12px] text-slate-400">{emptyMessage}</div>
      ) : (
        <ul
          id="goto-modal-list"
          className="m-0 min-h-0 flex-1 list-none overflow-auto p-[0.3rem]"
          role="listbox"
        >
          {suggestions.map((item, index) => (
            <li
              key={`${item.kind}-${item.step}-${item.title}`}
              id={`goto-item-${index}`}
              className={`flex items-center gap-[0.55rem] rounded-md px-[0.55rem] py-[0.45rem] text-slate-200 ${
                index === activeIndex
                  ? "bg-[color-mix(in_srgb,var(--accent)_22%,transparent)]"
                  : "hover:bg-[color-mix(in_srgb,var(--accent)_22%,transparent)]"
              }`}
              role="option"
              aria-selected={index === activeIndex}
              onPointerDown={event => {
                event.preventDefault();
                activate(index);
              }}
              onMouseEnter={() => setActiveIndex(index)}
            >
              {item.kind === "marker" ? (
                <span
                  className="size-[0.7rem] shrink-0 rounded-sm"
                  style={{ background: rowMarkerColorHex(item.color) }}
                />
              ) : (
                <span className="grid size-4 shrink-0 place-items-center rounded-[3px] bg-[color-mix(in_srgb,var(--accent)_18%,transparent)] text-[11px] font-bold text-lab-accent">
                  #
                </span>
              )}
              <span className="flex min-w-0 flex-col gap-[0.1rem]">
                <span className="truncate font-semibold">{item.title}</span>
                <span className="text-[11px] text-slate-400 tabular-nums">{item.detail}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
      <div className="shrink-0 border-t border-lab-line bg-lab-panel-bar px-3 pt-[0.4rem] pb-2 text-[11px] text-slate-500">
        Enter a <strong className="text-slate-400">row</strong> · search a{" "}
        <strong className="text-slate-400">marker</strong> ·{" "}
        <kbd className="mx-[0.1rem] inline-block rounded-[3px] border border-white/12 bg-black/25 px-[0.3rem] py-[0.05rem] text-[10px] text-slate-400">
          ↑
        </kbd>
        <kbd className="mx-[0.1rem] inline-block rounded-[3px] border border-white/12 bg-black/25 px-[0.3rem] py-[0.05rem] text-[10px] text-slate-400">
          ↓
        </kbd>{" "}
        ·{" "}
        <kbd className="mx-[0.1rem] inline-block rounded-[3px] border border-white/12 bg-black/25 px-[0.3rem] py-[0.05rem] text-[10px] text-slate-400">
          Enter
        </kbd>
      </div>
    </div>,
    document.body,
  );
}

function buildSuggestions(
  raw: string,
  steps: number,
  markers: ReturnType<typeof markersFromMap>,
): Suggestion[] {
  const query = raw.trim();
  const sorted = markers
    .slice()
    .sort((a, b) => a.step - b.step || a.label.localeCompare(b.label));
  const out: Suggestion[] = [];
  const digits = query.match(/^\d+$/);
  if (digits) {
    const step = Number(digits[0]);
    if (Number.isInteger(step) && step >= 0 && step < steps) {
      const at = sorted.find(m => m.step === step);
      out.push({
        kind: "row",
        step,
        title: `Go to row ${step}`,
        detail: at ? at.label : `of ${Math.max(0, steps - 1)}`,
      });
    }
  }

  const needle = query.toLowerCase();
  for (const marker of sorted) {
    if (
      needle
      && !marker.label.toLowerCase().includes(needle)
      && !String(marker.step).includes(needle)
    ) {
      continue;
    }
    out.push({
      kind: "marker",
      step: marker.step,
      title: marker.label,
      detail: `row ${marker.step}`,
      color: marker.color,
    });
  }

  return out;
}
